/**
 * API Client - Axios instance with JWT authentication
 * Handles token management, refresh, and request/response interceptors
 */

import axios from 'axios';
import { logger } from './logger';

// Base URL from environment or default
// const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

// Token storage keys
const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const USER_KEY = 'user';
const TENANT_KEY = 'tenant';

// Visitor token storage keys (for widget users)
// NOTE: These are base keys - actual storage uses tenant-prefixed keys
const VISITOR_TOKEN_KEY = 'visitor_token';
const VISITOR_ID_KEY = 'g6th_visitor_id';
const SESSION_ID_KEY = 'chat_session_id';
const WIDGET_TENANT_ID_KEY = 'widget_tenant_id';

// GLOBAL keys that persist across all sessions (never cleared)
// These are used for recovering visitor context after hard refresh
const GLOBAL_VISITOR_ID_KEY = 'g6th_global_visitor_id';
const GLOBAL_TENANT_ID_KEY = 'g6th_last_tenant_id';

// Legacy keys (for migration) - DO NOT include global keys here
const LEGACY_KEYS = [
    'visitor_token',
    'chat_session_id',
    'g6th_visitor_token',
    'g6th_tenant_id'
];

/**
 * Token management utilities
 */
export const tokenManager = {
    getAccessToken: () => localStorage.getItem(ACCESS_TOKEN_KEY),
    getRefreshToken: () => localStorage.getItem(REFRESH_TOKEN_KEY),

    setTokens: (accessToken, refreshToken) => {
        localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
        if (refreshToken) {
            localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
        }
    },

    clearTokens: () => {
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        localStorage.removeItem(TENANT_KEY);
    },

    hasTokens: () => !!localStorage.getItem(ACCESS_TOKEN_KEY),

    getUser: () => {
        const user = localStorage.getItem(USER_KEY);
        return user ? JSON.parse(user) : null;
    },

    setUser: (user) => {
        localStorage.setItem(USER_KEY, JSON.stringify(user));
    },

    getTenant: () => {
        const tenant = localStorage.getItem(TENANT_KEY);
        return tenant ? JSON.parse(tenant) : null;
    },

    setTenant: (tenant) => {
        if (tenant) {
            localStorage.setItem(TENANT_KEY, JSON.stringify(tenant));
        }
    },
};

// Create axios instance
const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    timeout: 120000, // 120 seconds to allow for LLM processing time
});

/**
 * Request interceptor - Attach JWT token
 */
api.interceptors.request.use(
    (config) => {
        const token = tokenManager.getAccessToken();

        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

/**
 * Response interceptor - Handle token refresh on 401
 */
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // If 401 and not already retrying
        if (error.response?.status === 401 && !originalRequest._retry) {
            // Don't retry or redirect for auth endpoints - let the error bubble up to show in UI
            const url = originalRequest.url || '';
            if (url.includes('/auth/login') ||
                url.includes('/auth/signup') ||
                url.includes('/auth/refresh') ||
                url.includes('/auth/super-admin/login') ||
                url.includes('/api/auth/login') ||
                url.includes('/api/auth/signup')) {
                // Just reject the error so the Login/Signup component can display it
                throw error;
            }

            if (isRefreshing) {
                // Queue this request while refresh is in progress
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                })
                    .then(token => {
                        originalRequest.headers.Authorization = `Bearer ${token}`;
                        return api(originalRequest);
                    })
                    .catch(err => { throw err; });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            const refreshToken = tokenManager.getRefreshToken();

            if (!refreshToken) {
                tokenManager.clearTokens();
                window.location.href = '/login';
                throw error;
            }

            try {
                // Call refresh endpoint
                const response = await axios.post(`${API_BASE_URL}/api/auth/refresh`, {
                    refresh_token: refreshToken,
                });

                const { access_token, refresh_token: newRefreshToken } = response.data;

                tokenManager.setTokens(access_token, newRefreshToken);

                // Update authorization header
                api.defaults.headers.common.Authorization = `Bearer ${access_token}`;
                originalRequest.headers.Authorization = `Bearer ${access_token}`;

                processQueue(null, access_token);

                return api(originalRequest);
            } catch (refreshError) {
                processQueue(refreshError, null);
                tokenManager.clearTokens();
                window.location.href = '/login';
                throw refreshError;
            } finally {
                isRefreshing = false;
            }
        }

        throw error;
    }
);

/**
 * Authentication API methods
 */
export const authAPI = {
    /**
     * Login with email and password
     */
    login: async (email, password) => {
        try {
            const response = await api.post('/api/auth/login', {
                email,
                password,
            });

            const { access_token, refresh_token, user, tenant } = response.data;

            tokenManager.setTokens(access_token, refresh_token);
            tokenManager.setUser(user);
            tokenManager.setTenant(tenant);

            return { success: true, user, tenant };
        } catch (error) {
            logger.error('Login error:', error.response?.data);
            throw error;
        }
    },

    /**
     * Signup new user (email is used for login) - Creates new tenant
     */
    signup: async (email, password, name, companyName) => {
        try {
            logger.log('🔍 SIGNUP REQUEST SENDING:', {
                email,
                name,
                company_name: companyName
            });
            const response = await api.post('/api/auth/signup', {
                email,
                password,
                name,
                company_name: companyName,
            });
            logger.log('✅ SIGNUP RESPONSE:', response.data);
            return { success: true, data: response.data };
        } catch (error) {
            logger.error('Signup error:', error.response?.data);
            throw error;
        }
    },

    /**
     * Signup as sub-admin - Joins existing tenant using tenant code
     */
    signupSubAdmin: async (email, password, name, tenantCode) => {
        try {
            logger.log('🔍 SUB-ADMIN SIGNUP REQUEST:', { email, name, tenantCode });
            const response = await api.post('/api/auth/signup-sub-admin', {
                email,
                password,
                name,
                tenant_code: tenantCode,
            });
            logger.log('✅ SUB-ADMIN SIGNUP RESPONSE:', response.data);
            return { success: true, data: response.data };
        } catch (error) {
            logger.error('Sub-admin signup error:', error.response?.data);
            throw error;
        }
    },

    /**
     * Logout current user
     */
    logout: async () => {
        try {
            await api.post('/api/auth/logout');
            tokenManager.clearTokens();
            return { success: true };
        } catch (error) {
            // Clear tokens even if API call fails
            tokenManager.clearTokens();
            logger.error('Logout error:', error);
            return { success: true };
        }
    },

    /**
     * Get current user profile
     */
    getCurrentUser: async () => {
        try {
            const response = await api.get('/api/auth/me');
            return response.data;
        } catch (error) {
            logger.error('Get current user error:', error);
            throw error;
        }
    },

    /**
     * Change password
     */
    changePassword: async (currentPassword, newPassword, confirmPassword) => {
        try {
            const response = await api.post('/api/auth/change-password', {
                current_password: currentPassword,
                new_password: newPassword,
                confirm_password: confirmPassword,
            });
            return response.data;
        } catch (error) {
            logger.error('Change password error:', error);
            throw error;
        }
    },

    /**
     * Update user activity
     */
    updateActivity: async () => {
        try {
            await api.post('/api/auth/activity');
        } catch (error) {
            logger.error('Update activity error:', error);
        }
    },
};

/**
 * Tenant API methods
 */
export const tenantAPI = {
    /**
     * Get current tenant details
     */
    getTenant: async (tenantId) => {
        try {
            const response = await api.get(`/api/tenants/${tenantId}`);
            return response.data;
        } catch (error) {
            logger.error('Get tenant error:', error);
            throw error;
        }
    },

    /**
     * Get tenant usage statistics
     */
    getUsage: async (tenantId) => {
        try {
            const response = await api.get(`/api/tenants/${tenantId}/usage`);
            return response.data;
        } catch (error) {
            logger.error('Get tenant usage error:', error);
            throw error;
        }
    },

    /**
     * Get widget settings for a tenant
     */
    getWidgetSettings: async (tenantId) => {
        try {
            const response = await api.get(`/api/tenants/${tenantId}/widget-settings`);
            return response.data;
        } catch (error) {
            logger.error('Get widget settings error:', error);
            throw error;
        }
    },

    /**
     * Update allowed domains for widget
     */
    updateAllowedDomains: async (tenantId, allowedDomains) => {
        try {
            const response = await api.put(`/api/tenants/${tenantId}/widget-settings/allowed-domains`, {
                allowed_domains: allowedDomains,
            });
            return response.data;
        } catch (error) {
            logger.error('Update allowed domains error:', error);
            throw error;
        }
    },

    /**
     * Regenerate widget keys (WARNING: invalidates existing integrations)
     */
    regenerateWidgetKeys: async (tenantId) => {
        try {
            const response = await api.post(`/api/tenants/${tenantId}/regenerate-widget-keys`);
            return response.data;
        } catch (error) {
            logger.error('Regenerate widget keys error:', error);
            throw error;
        }
    },

    /**
     * Get webhook settings for a tenant
     */
    getWebhookSettings: async (tenantId) => {
        try {
            const response = await api.get(`/api/tenants/${tenantId}/webhook-settings`);
            return response.data;
        } catch (error) {
            logger.error('Get webhook settings error:', error);
            throw error;
        }
    },

    /**
     * Update webhook settings for a tenant
     */
    updateWebhookSettings: async (tenantId, webhookSettings) => {
        try {
            const response = await api.put(`/api/tenants/${tenantId}/webhook-settings`, webhookSettings);
            return response.data;
        } catch (error) {
            logger.error('Update webhook settings error:', error);
            throw error;
        }
    },

    /**
     * Test webhook configuration
     */
    testWebhook: async (tenantId) => {
        try {
            const response = await api.post(`/api/tenants/${tenantId}/webhook-settings/test`);
            return response.data;
        } catch (error) {
            logger.error('Test webhook error:', error);
            throw error;
        }
    },

    /**
     * Get tenant-specific settings (scraping, widget behavior, etc.)
     */
    getTenantSettings: async (tenantId) => {
        try {
            const response = await api.get(`/api/tenants/${tenantId}/settings`);
            return response.data;
        } catch (error) {
            logger.error('Get tenant settings error:', error);
            throw error;
        }
    },

    /**
     * Update tenant-specific settings
     */
    updateTenantSettings: async (tenantId, settings) => {
        try {
            const response = await api.put(`/api/tenants/${tenantId}/settings`, settings);
            return response.data;
        } catch (error) {
            logger.error('Update tenant settings error:', error);
            throw error;
        }
    },

    /**
     * Download pre-configured WordPress plugin
     * Returns a Blob containing the ZIP file
     */
    downloadWordPressPlugin: async (tenantId, widgetUrl) => {
        try {
            const response = await api.get(`/api/tenants/${tenantId}/wordpress-plugin`, {
                params: { widget_url: widgetUrl },
                responseType: 'blob',
            });
            return response.data;
        } catch (error) {
            logger.error('Download WordPress plugin error:', error);
            throw error;
        }
    },

    // Offline Messages (admin inbox for lead capture forms)
    getOfflineMessages: async (tenantId, params = {}) => {
        try {
            const response = await api.get(`/api/tenants/${tenantId}/offline-messages`, { params });
            return response.data;
        } catch (error) {
            logger.error('Get offline messages error:', error);
            throw error;
        }
    },

    markOfflineMessageRead: async (tenantId, messageId) => {
        try {
            const response = await api.put(`/api/tenants/${tenantId}/offline-messages/${messageId}/read`);
            return response.data;
        } catch (error) {
            logger.error('Mark offline message read error:', error);
            throw error;
        }
    },

    archiveOfflineMessage: async (tenantId, messageId) => {
        try {
            const response = await api.delete(`/api/tenants/${tenantId}/offline-messages/${messageId}`);
            return response.data;
        } catch (error) {
            logger.error('Archive offline message error:', error);
            throw error;
        }
    },

    getOfflineUnreadCount: async (tenantId) => {
        try {
            const response = await api.get(`/api/tenants/${tenantId}/offline-messages/unread-count`);
            return response.data;
        } catch (error) {
            logger.error('Get offline unread count error:', error);
            throw error;
        }
    },
};

/**
 * Visitor Token Manager - For widget visitors (not logged-in users)
 * These tokens are short-lived (15 min) and auto-refresh
 *
 * IMPORTANT: All storage is now tenant-specific to prevent cross-tenant session leakage.
 * Keys are prefixed with tenant ID: `{tenantId}_visitor_token`, etc.
 */
export const visitorTokenManager = {
    // Internal: current tenant context
    _currentTenantId: null,

    // Set the current tenant context (call this before other operations)
    setTenantContext: (tenantId) => {
        visitorTokenManager._currentTenantId = tenantId;
    },

    // Get tenant-specific storage key
    _getKey: (baseKey) => {
        const tenantId = visitorTokenManager._currentTenantId;
        if (!tenantId) {
            // Fallback to global key (for backwards compatibility during init)
            return baseKey;
        }
        return `${tenantId}_${baseKey}`;
    },

    // Clear all legacy (non-tenant-specific) keys
    clearLegacyKeys: () => {
        LEGACY_KEYS.forEach(key => {
            localStorage.removeItem(key);
        });
        logger.log('[visitorTokenManager] Cleared legacy storage keys');
    },

    getToken: () => {
        const key = visitorTokenManager._getKey(VISITOR_TOKEN_KEY);
        return localStorage.getItem(key);
    },

    getVisitorId: () => {
        // Try tenant-specific key first
        const key = visitorTokenManager._getKey(VISITOR_ID_KEY);
        const tenantSpecific = localStorage.getItem(key);
        if (tenantSpecific) return tenantSpecific;

        // Fallback to global key (for hard refresh recovery)
        return localStorage.getItem(GLOBAL_VISITOR_ID_KEY);
    },

    getSessionId: () => {
        const key = visitorTokenManager._getKey(SESSION_ID_KEY);
        return localStorage.getItem(key);
    },

    getTenantId: () => {
        return visitorTokenManager._currentTenantId || localStorage.getItem(WIDGET_TENANT_ID_KEY);
    },

    setToken: (token, visitorId, sessionId, tenantId) => {
        // Update tenant context
        if (tenantId) {
            visitorTokenManager._currentTenantId = tenantId;
            // Store tenant ID globally (to know which tenant was last used)
            localStorage.setItem(WIDGET_TENANT_ID_KEY, tenantId);
            // Also store in global key for hard refresh recovery
            localStorage.setItem(GLOBAL_TENANT_ID_KEY, tenantId);
        }

        // Clear legacy keys on first use
        visitorTokenManager.clearLegacyKeys();

        // Store with tenant-specific keys
        const tokenKey = visitorTokenManager._getKey(VISITOR_TOKEN_KEY);
        const visitorKey = visitorTokenManager._getKey(VISITOR_ID_KEY);
        const sessionKey = visitorTokenManager._getKey(SESSION_ID_KEY);

        localStorage.setItem(tokenKey, token);
        if (visitorId) {
            localStorage.setItem(visitorKey, visitorId);
            // IMPORTANT: Also store visitor_id globally for hard refresh recovery
            // This key is NEVER cleared and persists across all sessions
            localStorage.setItem(GLOBAL_VISITOR_ID_KEY, visitorId);
        }
        if (sessionId) localStorage.setItem(sessionKey, sessionId);

        logger.log('[visitorTokenManager] Stored token for tenant:', tenantId, 'visitor:', visitorId);
    },

    clearToken: () => {
        const tokenKey = visitorTokenManager._getKey(VISITOR_TOKEN_KEY);
        const sessionKey = visitorTokenManager._getKey(SESSION_ID_KEY);

        localStorage.removeItem(tokenKey);
        localStorage.removeItem(sessionKey);
        // Keep visitor_id for returning visitors
    },

    // Clear only the session ID (keeps token and visitor ID intact)
    clearSessionId: () => {
        const sessionKey = visitorTokenManager._getKey(SESSION_ID_KEY);
        localStorage.removeItem(sessionKey);
    },

    // Clear ALL data for current tenant (full reset)
    clearAll: () => {
        const tokenKey = visitorTokenManager._getKey(VISITOR_TOKEN_KEY);
        const visitorKey = visitorTokenManager._getKey(VISITOR_ID_KEY);
        const sessionKey = visitorTokenManager._getKey(SESSION_ID_KEY);

        localStorage.removeItem(tokenKey);
        localStorage.removeItem(visitorKey);
        localStorage.removeItem(sessionKey);
    },

    hasToken: () => {
        const key = visitorTokenManager._getKey(VISITOR_TOKEN_KEY);
        return !!localStorage.getItem(key);
    },

    /**
     * Restore tenant context from localStorage on hard refresh
     * This should be called at the start of widget initialization
     * Returns context object if found, false otherwise
     */
    restoreContextFromStorage: () => {
        // Try to get tenant ID from multiple sources
        let storedTenantId = localStorage.getItem(WIDGET_TENANT_ID_KEY);
        if (!storedTenantId) {
            storedTenantId = localStorage.getItem(GLOBAL_TENANT_ID_KEY);
        }
        logger.log('[visitorTokenManager] restoreContextFromStorage - storedTenantId:', storedTenantId);

        // Try to get visitor ID from multiple sources
        // Priority: 1. Global key (never cleared), 2. Tenant-specific key
        let visitorId = localStorage.getItem(GLOBAL_VISITOR_ID_KEY);
        logger.log('[visitorTokenManager] Global visitor ID:', visitorId);

        if (!storedTenantId && !visitorId) {
            logger.log('[visitorTokenManager] No stored context found');
            return false;
        }

        // Set the tenant context if we have it
        if (storedTenantId) {
            visitorTokenManager._currentTenantId = storedTenantId;
        }

        // Check if we have a token for this tenant
        let hasToken = false;
        if (storedTenantId) {
            const tokenKey = `${storedTenantId}_${VISITOR_TOKEN_KEY}`;
            hasToken = !!localStorage.getItem(tokenKey);
            logger.log('[visitorTokenManager] Token key:', tokenKey, 'hasToken:', hasToken);

            // If no global visitor ID, try tenant-specific
            if (!visitorId) {
                const visitorKey = `${storedTenantId}_${VISITOR_ID_KEY}`;
                visitorId = localStorage.getItem(visitorKey);
                logger.log('[visitorTokenManager] Tenant-specific visitor ID:', visitorId);
            }
        }

        return {
            tenantId: storedTenantId,
            hasToken: hasToken,
            visitorId: visitorId
        };
    },

    /**
     * Get the globally stored visitor ID (for hard refresh recovery)
     */
    getGlobalVisitorId: () => {
        return localStorage.getItem(GLOBAL_VISITOR_ID_KEY);
    },

    /**
     * Get the last used tenant ID (for hard refresh recovery)
     */
    getLastTenantId: () => {
        return localStorage.getItem(GLOBAL_TENANT_ID_KEY) || localStorage.getItem(WIDGET_TENANT_ID_KEY);
    },
};

/**
 * Widget API - For chat widget initialization and visitor authentication
 * These endpoints don't require user login, only widget_public_key
 */
export const widgetAPI = {
    /**
     * Get complete chat history for the current visitor
     * Returns all sessions with messages for displaying history
     */
    getChatHistory: async (includeClosed = true) => {
        const token = visitorTokenManager.getToken();
        if (!token) {
            throw new Error('No visitor token available');
        }

        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/widget/visitor/chat-history`,
                {
                    params: { include_closed: includeClosed },
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            return {
                success: true,
                ...response.data,
            };
        } catch (error) {
            logger.error('Get chat history error:', error.response?.data || error);
            throw error;
        }
    },

    /**
     * Get all sessions for the current visitor
     */
    getVisitorSessions: async (limit = 50) => {
        const token = visitorTokenManager.getToken();
        if (!token) {
            throw new Error('No visitor token available');
        }

        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/widget/visitor/sessions`,
                {
                    params: { limit },
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            return {
                success: true,
                ...response.data,
            };
        } catch (error) {
            logger.error('Get visitor sessions error:', error.response?.data || error);
            throw error;
        }
    },

    /**
     * Get messages for a specific session
     */
    getSessionMessages: async (sessionId) => {
        const token = visitorTokenManager.getToken();
        if (!token) {
            throw new Error('No visitor token available');
        }

        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/widget/visitor/session/${sessionId}/messages`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            return {
                success: true,
                ...response.data,
            };
        } catch (error) {
            logger.error('Get session messages error:', error.response?.data || error);
            throw error;
        }
    },

    /**
     * Get the host domain where the widget is actually embedded
     * This is important for domain validation - we need to know the actual site domain,
     * not the iframe's origin
     */
    getHostDomain: () => {
        // Check for hostUrl in URL params (passed by widget-embed.js)
        const urlParams = new URLSearchParams(window.location.search);
        const hostUrl = urlParams.get('hostUrl');
        if (hostUrl) {
            try {
                const decoded = decodeURIComponent(hostUrl);
                const parsed = new URL(decoded);
                return parsed.hostname;
            } catch (e) {
                logger.warn('Failed to parse hostUrl:', e);
            }
        }

        // If running in iframe, try to get parent's location (will fail due to same-origin policy)
        // but document.referrer might work
        if (window.parent !== window && document.referrer) {
            try {
                const parsed = new URL(document.referrer);
                return parsed.hostname;
            } catch (e) {
                logger.warn('Failed to parse referrer:', e);
            }
        }

        // Fallback to current location (for direct widget preview)
        return window.location.hostname;
    },

    /**
     * Initialize widget and get visitor token
     * Call this when widget loads
     */
    initialize: async (widgetPublicKey, existingVisitorId = null) => {
        try {
            // Get the actual host domain where widget is embedded
            const hostDomain = widgetAPI.getHostDomain();
            logger.log('[widgetAPI] Initializing with host_domain:', hostDomain);

            const response = await axios.post(`${API_BASE_URL}/api/widget/init`, {
                widget_public_key: widgetPublicKey,
                visitor_id: existingVisitorId,
                host_domain: hostDomain,  // Send host domain for validation
            });

            const { token, visitor_id, session_id, tenant_id, expires_in, config } = response.data;

            // Store visitor token
            visitorTokenManager.setToken(token, visitor_id, session_id, tenant_id);

            return {
                success: true,
                token,
                visitorId: visitor_id,
                sessionId: session_id,
                tenantId: tenant_id,
                expiresIn: expires_in,
                config: config || null,  // Widget configuration from init response
            };
        } catch (error) {
            logger.error('Widget init error:', error.response?.data || error);
            throw error;
        }
    },

    /**
     * Refresh visitor token
     * Call this proactively before token expires
     */
    refreshToken: async () => {
        const currentToken = visitorTokenManager.getToken();
        if (!currentToken) {
            throw new Error('No visitor token to refresh');
        }

        try {
            const response = await axios.post(
                `${API_BASE_URL}/api/widget/refresh-token`,
                {},
                {
                    headers: {
                        Authorization: `Bearer ${currentToken}`,
                    },
                }
            );

            const { token, visitor_id, session_id, tenant_id, expires_in } = response.data;

            // Update stored token
            visitorTokenManager.setToken(token, visitor_id, session_id, tenant_id);

            return {
                success: true,
                token,
                visitorId: visitor_id,
                sessionId: session_id,
                tenantId: tenant_id,
                expiresIn: expires_in,
            };
        } catch (error) {
            logger.error('Token refresh error:', error.response?.data || error);
            // Clear invalid token
            visitorTokenManager.clearToken();
            throw error;
        }
    },

    /**
     * Validate current visitor token
     */
    validateToken: async () => {
        const currentToken = visitorTokenManager.getToken();
        if (!currentToken) {
            return { valid: false };
        }

        try {
            const response = await axios.get(`${API_BASE_URL}/api/widget/validate-token`, {
                headers: {
                    Authorization: `Bearer ${currentToken}`,
                },
            });

            return { valid: true, ...response.data };
        } catch (error) {
            return { valid: false };
        }
    },

    /**
     * Get axios config with visitor token
     * Use this for chat API calls from widget
     */
    getAuthConfig: () => {
        const token = visitorTokenManager.getToken();
        if (!token) {
            return {};
        }
        return {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        };
    },

    submitOfflineMessage: async (visitorName, visitorEmail, message) => {
        const token = visitorTokenManager.getToken();
        if (!token) {
            throw new Error('No visitor token available');
        }

        try {
            const response = await axios.post(
                `${API_BASE_URL}/api/widget/offline-message`,
                { visitor_name: visitorName, visitor_email: visitorEmail, message },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            return { success: true, ...response.data };
        } catch (error) {
            logger.error('Submit offline message error:', error.response?.data || error);
            throw error;
        }
    },
};

/**
 * Create axios instance for widget API calls (uses visitor token)
 */
export const widgetApiClient = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    timeout: 120000, // 120 seconds to allow for LLM processing time
});

// Request interceptor for widget API - attach visitor token
widgetApiClient.interceptors.request.use(
    (config) => {
        const token = visitorTokenManager.getToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Response interceptor for widget API - auto-refresh on 401
let isWidgetRefreshing = false;
let widgetFailedQueue = [];

const processWidgetQueue = (error, token = null) => {
    widgetFailedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    widgetFailedQueue = [];
};

widgetApiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
            // Don't retry for init endpoint
            if (originalRequest.url?.includes('/widget/init')) {
                throw error;
            }

            if (isWidgetRefreshing) {
                return new Promise((resolve, reject) => {
                    widgetFailedQueue.push({ resolve, reject });
                })
                    .then(token => {
                        originalRequest.headers.Authorization = `Bearer ${token}`;
                        return widgetApiClient(originalRequest);
                    })
                    .catch(err => { throw err; });
            }

            originalRequest._retry = true;
            isWidgetRefreshing = true;

            try {
                const result = await widgetAPI.refreshToken();
                processWidgetQueue(null, result.token);
                originalRequest.headers.Authorization = `Bearer ${result.token}`;
                return widgetApiClient(originalRequest);
            } catch (refreshError) {
                processWidgetQueue(refreshError, null);
                throw refreshError;
            } finally {
                isWidgetRefreshing = false;
            }
        }

        throw error;
    }
);

export default api;
